import {SignalsData} from "./signals.data";
import {
    IQuoteFull,
    LinePoints,
    MACD,
    MinMaxType, Point,
    PointUpDown,
    SignalDirectionType,
    StatsData
} from "../interface/stock";
import {StockSignalsEnum} from "../dict";
import {MathEx} from "../utils/math.ex";
import {IPredictionAdmin} from "../interface";
import {MinMaxData, MinMaxStats} from "./min-max.data";
import {SupResLine} from "./sup-res-lines.data";

export interface CumulativeStatsLines {
    last5predsAvg: LinePoints,
    last15minAvg: LinePoints,
    signals4h: LinePoints,
    macd: LinePoints,
    vwap: LinePoints,
    last30minAvg: LinePoints,
    last3predsAvg: LinePoints,
    last1hAvg: LinePoints,

    ema6: LinePoints,
    ema12: LinePoints,

    last2hAvg: LinePoints,
    last3hAvg: LinePoints,

    last4hAvg: LinePoints,

    // predVals: LinePoints,
    ema26: LinePoints,

    signals2h: LinePoints,
    signalsLast3p: LinePoints,
    signalsLast15m: LinePoints,
    signalsMacdCross: LinePoints,
    signalsVwapPrice: LinePoints,
    signalsConsensus3Pred10m: LinePoints,
    signalsConsensus4Pred10m: LinePoints,
    signalsConsensus5Pred10m: LinePoints,
    signalsEma6Price: LinePoints,
    signalsEma12Price: LinePoints,
    signalsEma26Price: LinePoints,

    signals2hMinMax220pctStockPred: LinePoints,
    signals2hMinMax220pctPred: LinePoints,
    signals2hMinMax160pctStock: LinePoints,
    signals2hMinMax220pctStock: LinePoints,
    signals2hMinMax160pctStockInside: LinePoints,
    signals2hMinMax220pctStockInside: LinePoints,

    signalsLast2hAvgStock: LinePoints,

    // signalTrendLines: LinePoints,
    openLine: LinePoints, // range start point, 2 points to draw line
    prevCloseLine: LinePoints, // prev range close line, 2 points

    supResLines: SupResLine[],
}

export interface CumulativeStatsResult {
    signals: {
        [StockSignalsEnum.SIGNALS_CONSENSUS_3PRED_10M]: PointUpDown[],
        [StockSignalsEnum.SIGNALS_CONSENSUS_4PRED_10M]: PointUpDown[],
        [StockSignalsEnum.SIGNALS_CONSENSUS_5PRED_10M]: PointUpDown[],
        [StockSignalsEnum.SIGNALS_MACD_CROSS]: PointUpDown[],
        [StockSignalsEnum.SIGNALS_EMA6_PRICE]: PointUpDown[],
        [StockSignalsEnum.SIGNALS_EMA12_PRICE]: PointUpDown[],
        [StockSignalsEnum.SIGNALS_EMA26_PRICE]: PointUpDown[],
        [StockSignalsEnum.SIGNALS_4H]: PointUpDown[],
        [StockSignalsEnum.SIGNALS_VWAP_PRICE]: PointUpDown[],
        [StockSignalsEnum.SIGNALS_2H]: PointUpDown[],
        [StockSignalsEnum.SIGNALS_LAST_15MIN]: PointUpDown[],
        [StockSignalsEnum.SIGNALS_LAST_3PRED]: PointUpDown[],
        [StockSignalsEnum.SIGNALS_2H_MIN_MAX_220PCT_PRED]: PointUpDown[],
        [StockSignalsEnum.SIGNALS_2H_MIN_MAX_160PCT_STOCK]: PointUpDown[],
        [StockSignalsEnum.SIGNALS_2H_MIN_MAX_220PCT_STOCK]: PointUpDown[],
        [StockSignalsEnum.SIGNALS_2H_MIN_MAX_160PCT_STOCK_INSIDE]: PointUpDown[],
        [StockSignalsEnum.SIGNALS_2H_MIN_MAX_220PCT_STOCK_INSIDE]: PointUpDown[],
        [StockSignalsEnum.SIGNALS_2H_MIN_MAX_220PCT_STOCK_3PRED]: PointUpDown[],
        [StockSignalsEnum.SIGNALS_SUP_RES_1H_STOCK]: PointUpDown[],
        [StockSignalsEnum.SIGNALS_OPEN_LINE_STOCK]: PointUpDown[],
        [StockSignalsEnum.SIGNALS_PREV_ClOSE_LINE_STOCK]: PointUpDown[],
        [StockSignalsEnum.SIGNALS_LAST_2H_AVG_STOCK]: PointUpDown[],
    }
    yMax: any,
    yMin: any,
    minMaxStats: MinMaxStats,
    cumulStats: CumulativeStatsLines,
    old: {
        // min/max 4h avg
        min2hAvg:    LinePoints,
        max2hAvg:    LinePoints,
        min2h60Avg:    LinePoints,
        max2h60Avg:    LinePoints,
        min2h220pctAvg:    LinePoints,
        max2h220pctAvg:    LinePoints, // 220%
    }
}

export class CumulativeData {

    public static calcSignalsConsensusNPredMmin(items: Readonly<IPredictionAdmin[]>, currPos:number, untilSecs:number, minPreds:number, destPoints: PointUpDown[]) {
        let count = 0;
        let sum = 0;
        let currPredDir = 0;
        for (let y = currPos; y >= 0; y--) {
            const py = items[y];
            if (py.valueTime<untilSecs) {
                break;
            }
            const stockVal = py.valueStock;
            if (stockVal) {
                const predDir = CumulativeData.direction(py.value, stockVal);
                count++;
                sum+=predDir;
                if (currPos===y) {
                    currPredDir = predDir;
                }
            }
        }
        if (count>=minPreds && sum!==0 && currPredDir===sum/Math.abs(sum)) {
            destPoints.push({
                x: items[currPos].valueTime,
                y: items[currPos].valueStock,
                isSellSignal: sum < 0
            });
        }
    };

    /**
     *
     * @param stats
     * @param stockLine
     * @param prevClose prev range close value, necessary for signal's line
     */
    public static calcCumulativeChartData(stats:StatsData, stockLine: LinePoints, params:{
        prevClose: number,
        prevCloseThreshold: number, // a rate like 0.001
    }): CumulativeStatsResult {

        const signalsConsensus3Pred10m: PointUpDown[] = [];
        const signalsConsensus4Pred10m: PointUpDown[] = [];
        const signalsConsensus5Pred10m: PointUpDown[] = [];
        const emptyLine = ()=>CumulativeData.emptyLine();
        const cumulStats:CumulativeStatsLines = {
            // predVals: emptyLine(),
            last5predsAvg: emptyLine(),
            last3predsAvg: emptyLine(),

            signals4h: emptyLine(),
            signals2h: emptyLine(),
            signals2hMinMax220pctStockPred: emptyLine(),
            signals2hMinMax220pctPred: emptyLine(),
            signals2hMinMax160pctStock: emptyLine(),
            signals2hMinMax220pctStock: emptyLine(),
            signals2hMinMax160pctStockInside: emptyLine(),
            signals2hMinMax220pctStockInside: emptyLine(),
            signalsLast3p: emptyLine(),
            signalsLast15m: emptyLine(),
            signalsMacdCross: emptyLine(),
            signalsEma6Price: emptyLine(),
            signalsEma12Price: emptyLine(),
            signalsEma26Price: emptyLine(),
            signalsVwapPrice: emptyLine(),
            signalsConsensus3Pred10m: emptyLine(),
            signalsConsensus4Pred10m: emptyLine(),
            signalsConsensus5Pred10m: emptyLine(),
            signalsLast2hAvgStock: emptyLine(),

            // signalsSupRes1hStock: emptyLine(),
            last15minAvg: emptyLine(),
            last30minAvg: emptyLine(),
            last1hAvg: emptyLine(),
            last2hAvg: emptyLine(),
            last3hAvg: emptyLine(),
            last4hAvg: emptyLine(),

            macd: emptyLine(),
            ema6: emptyLine(),
            ema12: emptyLine(),
            ema26: emptyLine(),
            // https://www.barchart.com/education/technical-indicators/vwap
            vwap: emptyLine(),
            supResLines: [] as SupResLine[],
            // signalTrendLines: emptyLine(),
            openLine: emptyLine(),
            prevCloseLine: emptyLine(),
        };
        const isEmpty = stats.stockStats.length===0
        if (!isEmpty) {
            const first = stats.stockStats[0];
            const last = stats.stockStats[stats.stockStats.length - 1];
            const startTime = first.t;
            const lastTime = last.t;

            const addLine = (l:LinePoints, v:number) =>  {
                l.x.push(startTime);
                l.y.push(v);
                l.x.push(lastTime);
                l.y.push(v);
            }
            addLine(cumulStats.openLine, first.c);
            addLine(cumulStats.prevCloseLine, params.prevClose);
        }


        const mData = this.calcMACD(stockLine);

        let mMin = null as any;
        let mMax = null as any;
        mData.forEach(v=> {
            const macd = cumulStats.macd;
            macd.x.push(v.time);
            macd.y.push(v.MACD);

            mMin = mMin !== null ? Math.min(mMin, v.MACD) : v.MACD;
            mMax = mMax !== null ? Math.min(mMax, v.MACD) : v.MACD;

            cumulStats.ema6.x.push(v.time);
            cumulStats.ema6.y.push(v.EMA6);

            cumulStats.ema12.x.push(v.time);
            cumulStats.ema12.y.push(v.EMA12);

            cumulStats.ema26.x.push(v.time);
            cumulStats.ema26.y.push(v.EMA26);

        });
        cumulStats.macd.yMax = mMax;
        cumulStats.macd.yMin = mMin;

        // const xAvgLast5:number[] = [];
        // const yAvgLast5:number[] = [];
        let yMin = null;
        let yMax = null;
        const items = stats.predStats;
        const getValAvg = (pos:number)=>items[pos].value;

        const calcAvg = (st: (pos:number)=>number, startPos: number, condUntil: (pos: number) => boolean) => {
            let sum = 0;
            let count = 0;
            for (let y = startPos; y >= 0 && condUntil(y); y--) {
                count++;
                sum += st(y);
            }
            return sum / count;
        }

        const minMaxCumMap = MinMaxData.emptyMap();

        const minMaxCumMapOld = new Map<number, MinMaxType|any>(); // time-min/max map
        // const cum4hMap = new Map<number, {t:number,min:number,max:number}>();
        for (let i = 0; i < items.length; i++) {
            const p = items[i];
            // if (p.valueTime >= options.startTime && p.valueTime <= options.endTime) {
            const val = p.value;
            const time = p.valueTime;
            const deviation = p.valueDeviation;
            const deviationHalf = deviation / 2;

            this.calcSignalsConsensusNPredMmin(items, i, p.valueTime-600, 3, signalsConsensus3Pred10m);
            this.calcSignalsConsensusNPredMmin(items, i, p.valueTime-600, 4, signalsConsensus4Pred10m);
            this.calcSignalsConsensusNPredMmin(items, i, p.valueTime-600, 5, signalsConsensus5Pred10m);

            // cumulStats.predVals.x.push(time);
            // cumulStats.predVals.y.push(val);

            cumulStats.last5predsAvg.y.push(calcAvg(getValAvg, i, (y) => y >= i - 4));
            cumulStats.last5predsAvg.x.push(time);

            cumulStats.last3predsAvg.y.push(calcAvg(getValAvg, i, (y) => y >= i - 2));
            cumulStats.last3predsAvg.x.push(time);


            cumulStats.last15minAvg.y.push(calcAvg(getValAvg, i, (y) => items[y].valueTime >= time - 15 * 60));
            cumulStats.last15minAvg.x.push(time);

            cumulStats.last30minAvg.y.push(calcAvg(getValAvg, i, (y) => items[y].valueTime >= time - 30 * 60));
            cumulStats.last30minAvg.x.push(time);

            cumulStats.last1hAvg.y.push(calcAvg(getValAvg, i, (y) => items[y].valueTime >= time - 60 * 60));
            cumulStats.last1hAvg.x.push(time);

            cumulStats.last2hAvg.y.push(calcAvg(getValAvg, i, (y) => items[y].valueTime >= time - 120 * 60));
            cumulStats.last2hAvg.x.push(time);

            cumulStats.last3hAvg.y.push(calcAvg(getValAvg, i, (y) => items[y].valueTime >= time - 180 * 60));
            cumulStats.last3hAvg.x.push(time);

            cumulStats.last4hAvg.y.push(calcAvg(getValAvg, i, (y) => items[y].valueTime >= time - 240 * 60));
            cumulStats.last4hAvg.x.push(time);

            cumulStats.last4hAvg.y.push(calcAvg(getValAvg, i, (y) => items[y].valueTime >= time - 240 * 60));
            cumulStats.last4hAvg.x.push(time);

            MinMaxData.addValToMinMaxMap(time, minMaxCumMap, val, deviationHalf);
            yMin = Math.min(yMin || val, val);
            yMax = Math.max(yMax || val, val);

            const t180m = Math.ceil(time/1800)*1800;
            const item = minMaxCumMapOld.has(t180m) ? minMaxCumMapOld.get(t180m) as any : {minSum:0,maxSum:0,count:0};
            minMaxCumMapOld.set(t180m, {
                minSum: item.minSum + (val-deviationHalf),
                maxSum: item.maxSum + (val+deviationHalf),
                count: item.count+1,
            });

            // }
        }

        const calcSignal = (cur:IQuoteFull,prevP:IQuoteFull,val:number) => {
            if (cur.c>=val && prevP.c<val) {
                return {
                    x: cur.t,
                    y: cur.c,
                    isSellSignal: false,
                } as PointUpDown;
            }
            if (cur.c<val && prevP.c>=val) {
                return {
                    x: cur.t,
                    y: cur.c,
                    isSellSignal: true,
                } as PointUpDown;
            }
            return null;
        }
        const openLineSignals:PointUpDown[] = [];
        const prevCloseLineSignals:PointUpDown[] = [];
        let prevDPoint:IQuoteFull = null as any;
        if (stats.stockStats.length>0) {
            const openPrice = cumulStats.openLine.y[0];
            let prevClosePrice = cumulStats.prevCloseLine.y[0];
            if (params.prevCloseThreshold) {
                // adjust prev close price
                prevClosePrice += (openPrice>=prevClosePrice?1:-1) * prevClosePrice*params.prevCloseThreshold;
            }
            for (const s of stats.stockStats) {
                if (prevDPoint) {
                    const sigPrev = calcSignal(s, prevDPoint, prevClosePrice);
                    if (sigPrev) {
                        prevCloseLineSignals.push(sigPrev);
                    }

                    const sigOpen = calcSignal(s, prevDPoint, openPrice);
                    if (sigOpen) {
                        openLineSignals.push(sigOpen);
                    }

                }
                prevDPoint = s;
            }
        }



        const oldMinMax = {
            // min/max 4h avg
            min2hAvg:    { x: [] as number[], y: [] as number[]} as LinePoints,
            max2hAvg:    { x: [] as number[], y: [] as number[]} as LinePoints,
            min2h60Avg:    { x: [] as number[], y: [] as number[]} as LinePoints,
            max2h60Avg:    { x: [] as number[], y: [] as number[]} as LinePoints,
            min2h220pctAvg:    { x: [] as number[], y: [] as number[]} as LinePoints,
            max2h220pctAvg:    { x: [] as number[], y: [] as number[]} as LinePoints, // 220%
        }

        minMaxCumMapOld.forEach((v, k)=>{
            let min4Cum = 0;
            let max4Cum = 0;
            let count4 = 0;

            let min2Cum = 0;
            let max2Cum = 0;
            let count2 = 0;

            minMaxCumMapOld.forEach((v2, k2)=>{
                if (k2>k-240*60 && k2<=k) {
                    min4Cum+=v2.minSum;
                    max4Cum+=v2.maxSum;
                    count4+=v2.count;
                }

                if (k2>k-120*60 && k2<=k) {
                    min2Cum+=v2.minSum;
                    max2Cum+=v2.maxSum;
                    count2+=v2.count;
                }

            });
            const min4 = min4Cum/count4;
            const max4 = max4Cum/count4;
            // tslint:disable-next-line:variable-name
            const band4h60pct = (max4-min4)*0.6;


            // 2h data
            const min2 = min2Cum/count2;
            const max2 = max2Cum/count2;
            // tslint:disable-next-line:variable-name
            const band2h160pct = (max2-min2)*0.6;
            const band2h220pct = (max2-min2)*1.2;

            oldMinMax.min2hAvg.y.push(min2);
            oldMinMax.min2hAvg.x.push(k);
            oldMinMax.max2hAvg.y.push(max2);
            oldMinMax.max2hAvg.x.push(k);

            oldMinMax.min2h60Avg.y.push(min2-band2h160pct);
            oldMinMax.min2h60Avg.x.push(k);
            oldMinMax.max2h60Avg.y.push(max2+band2h160pct);
            oldMinMax.max2h60Avg.x.push(k);

            oldMinMax.min2h220pctAvg.y.push(min2-band2h220pct);
            oldMinMax.min2h220pctAvg.x.push(k);
            oldMinMax.max2h220pctAvg.y.push(max2+band2h220pct);
            oldMinMax.max2h220pctAvg.x.push(k);

        });


        const minMaxStats = MinMaxData.calcMinMax(minMaxCumMap);
        const addSignalToStats = (signals: PointUpDown[], p:LinePoints)=>{
            signals.forEach(s=>{
                p.x.push(s.x);
                p.y.push(s.y);
            });
        }
        // const toMinMaxLine = (min:LinePoints,max:LinePoints, rangeSecs: number, factor:number, points: MinMaxPointsMap)=>{
        //     return {min: min,max: max, rangeSecs: rangeSecs, factor: factor, points: points} as MinMaxLines;
        // };
        const signals4h = SignalsData.calcSignals(
                {x: cumulStats.last3predsAvg.x, y: cumulStats.last3predsAvg.y},
                stockLine,
                minMaxStats.stats4h160pctAvg,
                // toMinMaxLine(minMaxStats.min4h160pctAvg, minMaxStats.max4h160pctAvg, 240 * 60,0.6, minMaxCumMap),
        );
        addSignalToStats(signals4h, cumulStats.signals4h);

        const signals2h = SignalsData.calcSignals(

                {x: cumulStats.last3predsAvg.x, y: cumulStats.last3predsAvg.y},
                stockLine,
                minMaxStats.stats2h160pctAvg
                // toMinMaxLine(minMaxStats.min2h160pctAvg, minMaxStats.max2h160pctAvg, 120 * 60, 0.6, minMaxCumMap)
        );
        addSignalToStats(signals2h, cumulStats.signals2h);

        const signals2hMinMax220pctStock3Pred = SignalsData.calcSignals(
                {x: cumulStats.last3predsAvg.x, y: cumulStats.last3predsAvg.y},
                stockLine,
                minMaxStats.stats2h220pctAvg
                // toMinMaxLine(minMaxStats.min2h220pctAvg, minMaxStats.max2h220pctAvg, 120*60, 1.2, minMaxCumMap)
        );
        addSignalToStats(signals2hMinMax220pctStock3Pred, cumulStats.signals2hMinMax220pctStockPred);

        const signalSupResStock = SignalsData.calcSupResStock(stats.stockStats);
        // addSignalToStats(signalSupResStock.signals, cumulStats.signalsSupRes1hStock);
        cumulStats.supResLines = signalSupResStock.lines;

        const signals2hMinMax220pctPred = SignalsData.calcSignalsPredUpDownMinMax(stats.predStats,minMaxStats.stats2h220pctAvg);
        addSignalToStats(signals2hMinMax220pctPred, cumulStats.signals2hMinMax220pctPred);

        const signals2hMinMax220pctStock = SignalsData.calcSignalsStockUpDownMinMax(stockLine, minMaxStats.stats2h220pctAvg);
        addSignalToStats(signals2hMinMax220pctStock, cumulStats.signals2hMinMax220pctStock);

        const signals2hMinMax160pctStock = SignalsData.calcSignalsStockUpDownMinMax(stockLine,minMaxStats.stats2h160pctAvg);
        addSignalToStats(signals2hMinMax160pctStock, cumulStats.signals2hMinMax160pctStock);


        const signals2hMinMax160pctStockInside = SignalsData.calcSignalsStockUpDownMinMaxInside(stockLine,minMaxStats.stats2h160pctAvg);
        addSignalToStats(signals2hMinMax160pctStockInside, cumulStats.signals2hMinMax160pctStockInside);

        const signals2hMinMax220pctStockInside = SignalsData.calcSignalsStockUpDownMinMaxInside(stockLine,minMaxStats.stats2h220pctAvg);
        addSignalToStats(signals2hMinMax220pctStockInside, cumulStats.signals2hMinMax220pctStockInside);

        const signalsLast3pred = SignalsData.calcSignalUpDownStock(
                stockLine,[{x: cumulStats.last3predsAvg.x, y: cumulStats.last3predsAvg.y},],
        );
        addSignalToStats(signalsLast3pred, cumulStats.signalsLast3p);

        const signalsLast2hAvgStock = SignalsData.calcSignalCrossStock(
                stockLine,{x: cumulStats.last2hAvg.x, y: cumulStats.last2hAvg.y},
        );
        addSignalToStats(signalsLast2hAvgStock, cumulStats.signalsLast2hAvgStock);

        const signalsLast15m = SignalsData.calcSignalUpDownStock(
                stockLine,[{x: cumulStats.last15minAvg.x, y: cumulStats.last15minAvg.y},],
        );
        addSignalToStats(signalsLast15m, cumulStats.signalsLast15m);

        const vWap = this.calcVwap(stats.stockStats);
        addSignalToStats(vWap.signals, cumulStats.signalsVwapPrice);
        cumulStats.vwap = vWap.line;

        const emaSignals = this.calcEmaSignals(stockLine, cumulStats);
        addSignalToStats(emaSignals.ema6, cumulStats.signalsEma6Price);
        addSignalToStats(emaSignals.ema12, cumulStats.signalsEma12Price);
        addSignalToStats(emaSignals.ema26, cumulStats.signalsEma26Price);

        const signalsMacdCross: PointUpDown[] = [];
        const macDY = cumulStats.macd.y;
        for (let i = 25; i<macDY.length; i++) {
            const prev = macDY[i-1];
            const curr = macDY[i];
            const x = cumulStats.macd.x[i];
            const y = stockLine.y[i];
            if (prev<0 && curr>=0) {
                signalsMacdCross.push({x: x, y: y, isSellSignal: false});
            } else if (prev>0 && curr<=0) {
                signalsMacdCross.push({x: x, y: y, isSellSignal: true});
            }
        }
        addSignalToStats(signalsMacdCross, cumulStats.signalsMacdCross);

        addSignalToStats(signalsConsensus3Pred10m, cumulStats.signalsConsensus3Pred10m);
        addSignalToStats(signalsConsensus4Pred10m, cumulStats.signalsConsensus4Pred10m);
        addSignalToStats(signalsConsensus5Pred10m, cumulStats.signalsConsensus5Pred10m);

        return {
            cumulStats: cumulStats,
            minMaxStats: minMaxStats,
            signals: {
                [StockSignalsEnum.SIGNALS_2H]: signals2h,
                [StockSignalsEnum.SIGNALS_2H_MIN_MAX_220PCT_STOCK_3PRED]: signals2hMinMax220pctStock3Pred,
                [StockSignalsEnum.SIGNALS_4H]: signals4h,
                [StockSignalsEnum.SIGNALS_LAST_3PRED]: signalsLast3pred,
                [StockSignalsEnum.SIGNALS_LAST_15MIN]: signalsLast15m,
                [StockSignalsEnum.SIGNALS_MACD_CROSS]: signalsMacdCross,
                [StockSignalsEnum.SIGNALS_EMA6_PRICE]: emaSignals.ema6,
                [StockSignalsEnum.SIGNALS_EMA12_PRICE]: emaSignals.ema12,
                [StockSignalsEnum.SIGNALS_EMA26_PRICE]: emaSignals.ema26,
                [StockSignalsEnum.SIGNALS_VWAP_PRICE]: vWap.signals,
                [StockSignalsEnum.SIGNALS_CONSENSUS_3PRED_10M]: signalsConsensus3Pred10m,
                [StockSignalsEnum.SIGNALS_CONSENSUS_4PRED_10M]: signalsConsensus4Pred10m,
                [StockSignalsEnum.SIGNALS_CONSENSUS_5PRED_10M]: signalsConsensus5Pred10m,
                [StockSignalsEnum.SIGNALS_2H_MIN_MAX_220PCT_PRED]: signals2hMinMax220pctPred,
                [StockSignalsEnum.SIGNALS_2H_MIN_MAX_220PCT_STOCK]: signals2hMinMax220pctStock,
                [StockSignalsEnum.SIGNALS_2H_MIN_MAX_160PCT_STOCK]: signals2hMinMax160pctStock,
                [StockSignalsEnum.SIGNALS_2H_MIN_MAX_220PCT_STOCK_INSIDE]: signals2hMinMax220pctStockInside,
                [StockSignalsEnum.SIGNALS_2H_MIN_MAX_160PCT_STOCK_INSIDE]: signals2hMinMax160pctStockInside,
                [StockSignalsEnum.SIGNALS_SUP_RES_1H_STOCK]: signalSupResStock.signals,
                [StockSignalsEnum.SIGNALS_OPEN_LINE_STOCK]: openLineSignals,
                [StockSignalsEnum.SIGNALS_PREV_ClOSE_LINE_STOCK]: prevCloseLineSignals,
                [StockSignalsEnum.SIGNALS_LAST_2H_AVG_STOCK]: signalsLast2hAvgStock,
            },
            yMin, yMax,

            old: oldMinMax,
        };
    }

    public static calcVwap(stockData: Readonly<IQuoteFull[]>) {
        let volumeSum = 0;
        let priceVolSum = 0;
        // const line: Point[] = [];
        const d = {
            signals: [] as PointUpDown[],
            line: {x:[], y:[]} as LinePoints,
            min: null as any as number,
            max: null as any as number,
        };
        let prevDist:number = null as any;
        let prevTime:number = null as any;
        for (const q of stockData) {

            const close = q.c;
            const volume = q.v;
            const time = q.t;
            const price = (q.c+q.h+q.l)/3;
            // reset calc, it can be multi-day calc
            if (prevTime!==null && q.t-prevTime>3600) {
                volumeSum = 0;
                priceVolSum = 0;
                prevDist = null as any;
                prevTime = null as any;
            }


            volumeSum+=volume;
            priceVolSum += price*volume;
            const vwapPrice = MathEx.round(priceVolSum/volumeSum, 4);

            d.min = d.min !== null ? Math.min(d.min, vwapPrice) : vwapPrice;
            d.max = d.max !== null ? Math.max(d.max, vwapPrice) : vwapPrice;
            d.line.x.push(time);
            d.line.y.push(vwapPrice);
            const newDist = vwapPrice-close;
            if (prevDist!==null) {
                if (prevDist<0 && newDist>=0) {
                    // signal to buy?
                    d.signals.push({x: time,y: vwapPrice,isSellSignal: true,});
                } if (prevDist>=0 && newDist<0) {
                    // signal to sell?
                    d.signals.push({x: time,y: vwapPrice,isSellSignal: false});
                }
            }
            prevDist = vwapPrice-close;
            prevTime = time;
        }
        return d;
    }

    public static calcMACD(stockLine: LinePoints) {


        const lines: MACD[] = [];
        if (stockLine.x.length===0) {
            return lines;
        }

        // MACD=12-Period EMA − 26-Period EMA
        // EMA [today] = (Price [today] x K) + (EMA [yesterday] x (1 – K))
        //
        // Where:
        //
        //   K = 2 ÷(N + 1)
        // N = the length of the EMA
        // Price [today] = the current closing price
        // EMA [yesterday] = the previous EMA value
        // EMA [today] = the current EMA value
        const k26 = 2/(26-1);
        const k12 = 2/(12-1);
        const k6 = 2/(6-1);

        // use first closePrice to calc initial first point
        const first = stockLine.y[0];
        let prev:MACD = {
            time: stockLine.x[0],
            MACD: 0,
            EMA6: first,
            EMA12: first,
            EMA26: first,
        };
        lines.push(prev);

        for (let i=1; i<stockLine.x.length; i++) {

            const time = stockLine.x[i];
            const priceToday = stockLine.y[i];
            const ema6 = priceToday * k6 + prev.EMA6 * (1-k6);
            const ema12 = priceToday * k12 + prev.EMA12* (1-k12);
            const ema26 = priceToday * k26 + prev.EMA26* (1-k26);
            const macdToday:MACD = {
                time: time,
                MACD: ema12 - ema26,
                EMA6: ema6,
                EMA12: ema12,
                EMA26: ema26,
            };
            lines.push(macdToday);
            prev = macdToday;
        }
        return lines;
    }

    /**
     * 1 - UP, -1 - DOWN
     * @param predictedValue
     * @param stockValueAtPredMadeTime
     */
    public static direction(predictedValue:number, stockValueAtPredMadeTime:number) {
        return predictedValue>=stockValueAtPredMadeTime ? SignalDirectionType.UP : SignalDirectionType.DOWN;
    }

    public static emptyLine() {
        return { x: [] as number[], y: [] as number[]} as LinePoints;
    }

    public static calcEmaSignals(stockLine: LinePoints, cumulStats: CumulativeStatsLines) {
        const signalsEma6Price: PointUpDown[] = [];
        const signalsEma12Price: PointUpDown[] = [];
        const signalsEma26Price: PointUpDown[] = [];
        const stockY = stockLine.y;
        const ema6Y = cumulStats.ema6;
        const ema12Y = cumulStats.ema12;
        const ema26Y = cumulStats.ema26;

        const calcSignal = (ema: LinePoints, i: number, signals: PointUpDown[]) => {
            const x = stockLine.x[i];
            const prevEma = ema.y[i-1];
            const prevStock = stockY[i-1];
            const currEma = ema.y[i];
            const currStock = stockY[i];
            // console.debug({prevEma, prevStock, currEma, currStock});
            if (prevEma<prevStock && currEma>=currStock) {
                signals.push({x: x, y: currStock, isSellSignal: true});
            } else if (prevEma>prevStock && currEma<=currStock) {
                signals.push({x: x, y: currStock, isSellSignal: false});
            }
        }

        for (let i = 5; i<stockY.length; i++) {
            if (i>=5) {
                calcSignal(ema6Y, i, signalsEma6Price);
            }
            if (i>=11) {
                calcSignal(ema12Y, i, signalsEma12Price);
            }
            if (i>=25) {
                calcSignal(ema26Y, i, signalsEma26Price);
            }
            // const x = stockLine.x[i];
            // const prevEma = ema26Y[i-1];
            // const prevStock = stockY[i-1];
            // const currEma = ema26Y[i];
            // const currStock = stockY[i];
            // // console.debug({prevEma, prevStock, currEma, currStock});
            // if (prevEma<prevStock && currEma>=currStock) {
            //     signalsEma26Price.push({x: x, y: currStock, isSellSignal: true});
            // } else if (prevEma>prevStock && currEma<=currStock) {
            //     signalsEma26Price.push({x: x, y: currStock, isSellSignal: false});
            // }
        }
        return {
            ema6: signalsEma6Price,
            ema12: signalsEma12Price,
            ema26: signalsEma26Price,
        }
    }
}
